import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight, ChevronLeft, ChevronDown, CheckCircle2, ShieldCheck, Star,
  Users, FileSpreadsheet, Lock, BarChart3, Briefcase, Calculator, Network, Layers,
  Infinity as InfinityIcon
} from "lucide-react";
import SEO from "../seo/SEO";
import { useT } from "../seo/i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ORGANIZATION_SCHEMA, SOFTWARE_SCHEMA, buildBreadcrumbSchema, buildFAQSchema } from "../seo/seoConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const heading = { fontFamily: "'Satoshi', sans-serif" };

const FAQS = [
  { q: "Is InvoiceSetu free for CAs?", a: "Yes — the CA Workspace is free to set up and lets you manage all client books from one dashboard. Upgrade only when you need advanced filing automation or 50+ clients." },
  { q: "Do I get a 50% discount on the Growth plan?", a: "Yes — verified CAs get 50% off the Growth plan and a free CA Workspace. Email us your CA membership number from your registered address to activate." },
  { q: "Can I export GSTR-1 / 3B / 9 directly?", a: "Yes — GSTR-1 (JSON + Excel) is one click. GSTR-3B is auto-prepared with input tax credit reconciliation against GSTR-2B. GSTR-9 annual return is generated from the year's data." },
  { q: "How do my clients invite me?", a: "Each client adds your email under Settings → CA Access. You receive an invite, accept once, and they appear on your CA Workspace dashboard." },
  { q: "Can I bill clients through InvoiceSetu?", a: "Yes — issue your CA service invoices to clients from the same workspace. SAC 998222 is pre-loaded; TDS 10% under 194J is handled in net payable." },
  { q: "Is client data secure?", a: "Yes — encrypted at rest and in transit, daily backups, hosted on Indian servers. Each client controls their own access; you cannot see what they don't share." },
  { q: "Can I bulk-file for all my clients?", a: "Yes — the bulk-filing dashboard lists every client's status, due dates, missing documents and one-click queue for filing season." },
  { q: "What happens at year-end?", a: "Annual GSTR-9 + reconciliation runs from your dashboard. Client books, ITC mismatches, late fees — all surfaced in one consolidated view." },
];

export default function CALandingPage() {
  const { t, lang } = useT();
  const [caPricing, setCaPricing] = useState({ yearly: 6999, lifetime: 29999, banner_text: "" });
  useEffect(() => {
    axios.get(`${API}/pricing/public`).then(r => {
      if (r.data?.ca) setCaPricing({
        yearly: r.data.ca.yearly ?? 6999,
        lifetime: r.data.ca.lifetime ?? 29999,
        banner_text: r.data.ca.banner_text || "",
      });
    }).catch(() => {});
  }, []);
  return (
    <div className="bg-white min-h-screen" data-testid="ca-landing">
      <SEO
        title="InvoiceSetu for Chartered Accountants — Manage All Clients in One Workspace"
        description="Free CA Workspace for Indian Chartered Accountants. Manage all client invoices, exports, GSTR-1 filings and reconciliation from one dashboard. 50% Growth plan discount for verified CAs."
        path={lang === "en" ? "/for-ca" : `/${lang}/for-ca`}
        multilingual
        schemas={[
          ORGANIZATION_SCHEMA,
          SOFTWARE_SCHEMA,
          buildBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "For CAs", url: "/for-ca" }]),
          buildFAQSchema(FAQS),
        ]}
      />

      <div className="bg-white/90 backdrop-blur border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ChevronLeft className="w-4 h-4" /> InvoiceSetu
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <Link to="/auth" className="bg-blue-600 text-white font-semibold rounded-lg px-4 py-1.5 hover:bg-blue-700 text-xs sm:text-sm" data-testid="ca-cta-header">{t("ca.cta.primary")}</Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="px-4 sm:px-6 py-12 sm:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 mb-5">
            <Calculator className="w-3.5 h-3.5" /> {t("ca.hero.eyebrow")}
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-[1.05] mb-5"
            style={heading}>
            {t("ca.hero.h1.line1")}<br/>
            <span className="text-blue-600">{t("ca.hero.h1.line2")}</span>
          </motion.h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8">
            {t("ca.hero.subhead")}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-7">
            <Link to="/auth" data-testid="ca-cta-primary"
              className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-lg px-7 py-3.5 hover:bg-blue-700 transition-colors shadow-[0_4px_14px_rgba(37,99,235,0.25)]">
              {t("ca.cta.primary")} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:hello@invoicesetu.in?subject=CA%20Discount%20Activation"
              className="inline-flex items-center gap-2 bg-white border-2 border-slate-200 text-slate-900 font-semibold rounded-lg px-7 py-3.5 hover:border-slate-300 hover:bg-slate-50 transition-colors">
              {t("ca.cta.discount")}
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Free CA Workspace</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> 50% off Growth plan</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Bulk filing tools</span>
          </div>
        </div>
      </section>

      {/* Pain → Gain */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-10" style={heading}>
            What changes when your clients are on InvoiceSetu
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { before: "WhatsApp PDFs at 11pm on the 10th", after: "Every invoice already in your dashboard, indexed by client and date" },
              { before: "Excel sheets sent for GSTR-1 prep", after: "GSTR-1 JSON one click — uploaded to portal in 5 minutes" },
              { before: "Missing documents at year-end audit", after: "Edit-history audit trail with hash + timestamp on every invoice" },
              { before: "Tally exports + manual reconciliation", after: "GSTR-2B reconciliation auto-runs — only mismatches surface" },
              { before: "Chasing clients for sign-offs", after: "Client approves quotations & invoices online — you see status live" },
              { before: "Per-client logins, per-tool chaos", after: "One dashboard, every client, role-based access" },
            ].map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl border border-slate-100 p-4"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="w-5 h-5 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">×</span>
                  <p className="text-xs text-slate-500 line-through">{row.before}</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-800 font-medium">{row.after}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-2" style={heading}>
            Built for the way CAs actually work
          </h2>
          <p className="text-sm text-slate-500 text-center mb-10">Every feature designed with practising CAs — not finance MBAs.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { i: Users, t: "Multi-client dashboard", d: "One screen lists every client with status, ageing, pending GSTR, and missing documents." },
              { i: FileSpreadsheet, t: "Bulk GSTR-1 export", d: "Generate JSON + Excel for any month, any client, in one click. Upload to portal directly." },
              { i: Network, t: "GSTR-2B reconciliation", d: "Auto-match purchases against GSTR-2B. Only mismatches surface — saves hours per client." },
              { i: Briefcase, t: "Bill clients from same place", d: "Issue your CA service invoices (SAC 998222 pre-loaded). TDS 194J auto-handled." },
              { i: BarChart3, t: "Filing calendar", d: "Visual calendar of every client's GSTR-1, 3B, ITR, TDS deadlines. Auto-reminder 3 days before." },
              { i: Layers, t: "Annual return prep", d: "GSTR-9 + 9C generated from year's books. Mismatches with GSTR-3B flagged automatically." },
              { i: Lock, t: "Role-based access", d: "Articleship trainees see only what you assign. Audit log of every action — perfect for ICAI peer review." },
              { i: ShieldCheck, t: "Bank-grade security", d: "Encrypted at rest + in transit. Indian servers, daily backups. Client data never leaves the country." },
              { i: Star, t: "Native Tally compatibility", d: "Tally XML import / export — if your clients still use Tally, you don't have to rebuild books." },
            ].map((f, i) => {
              const Icon = f.i;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-white rounded-xl border border-slate-100 p-5 hover:border-blue-200 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1.5" style={heading}>{f.t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing — CA Professional plans */}
      <section className="px-4 sm:px-6 py-14 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white" data-testid="ca-pricing-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200 mb-3">Chartered Accountant Plans</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-3" style={heading}>
              One flat price. Unlimited clients.
            </h2>
            <p className="text-blue-100 max-w-xl mx-auto">
              {caPricing.banner_text || "Built for practising CAs — no per-client licence fee, ever."}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {/* Yearly */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white text-slate-900 rounded-2xl p-6 sm:p-7 shadow-2xl"
              data-testid="ca-plan-yearly"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold" style={heading}>Yearly</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black" style={heading}>₹{caPricing.yearly.toLocaleString("en-IN")}</span>
                <span className="text-sm text-slate-500">/ year</span>
              </div>
              <p className="text-xs text-slate-500 mb-5">Auto-renews annually · GST invoice provided</p>
              <ul className="space-y-2 text-sm text-slate-700 mb-6">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> Unlimited client workspaces</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> Bulk GSTR-1/3B/9 export</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> GSTR-2B reconciliation</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> White-label PDF branding</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> Priority CA support</li>
              </ul>
              <Link
                to="/auth?role=ca"
                className="block w-full text-center bg-blue-600 text-white font-bold rounded-lg px-6 py-3 hover:bg-blue-700 transition-colors"
                data-testid="ca-plan-yearly-cta"
              >
                Start Yearly Plan
              </Link>
            </motion.div>
            {/* Lifetime */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: 0.05 }}
              className="relative bg-slate-900 text-white rounded-2xl p-6 sm:p-7 shadow-2xl ring-2 ring-yellow-400/70"
              data-testid="ca-plan-lifetime"
            >
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-slate-900 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow">Best value</span>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-lg bg-yellow-400/20 text-yellow-300 flex items-center justify-center">
                  <InfinityIcon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold" style={heading}>Lifetime</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black" style={heading}>₹{caPricing.lifetime.toLocaleString("en-IN")}</span>
                <span className="text-sm text-slate-400">one-time</span>
              </div>
              <p className="text-xs text-slate-400 mb-5">Pay once · use forever · no renewals</p>
              <ul className="space-y-2 text-sm text-slate-200 mb-6">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> Everything in Yearly</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> Lifetime software access</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> All future updates included</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> 1-hour onboarding session</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> ICAI-verified badge on invoices</li>
              </ul>
              <Link
                to="/auth?role=ca"
                className="block w-full text-center bg-yellow-400 text-slate-900 font-bold rounded-lg px-6 py-3 hover:bg-yellow-300 transition-colors"
                data-testid="ca-plan-lifetime-cta"
              >
                Grab Lifetime Access
              </Link>
            </motion.div>
          </div>
          <p className="text-center text-xs text-blue-200 mt-6">
            Have questions? <a href="mailto:hello@invoicesetu.in?subject=CA%20Plan%20Enquiry" className="underline hover:text-white">hello@invoicesetu.in</a>
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="px-4 sm:px-6 py-14">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-8" style={heading}>FAQs for CAs</h2>
          <div className="space-y-2">
            {FAQS.map((f, i) => <Faq key={i} q={f.q} a={f.a} index={i} />)}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-8 px-4 text-center text-xs text-slate-400">
        Powered by <Link to="/" className="text-blue-600 font-semibold">InvoiceSetu</Link>
      </footer>
    </div>
  );
}

function Faq({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-white rounded-xl border border-slate-100" data-testid={`ca-faq-${index}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full text-left px-5 py-4 flex items-center justify-between gap-3">
        <span className="font-semibold text-slate-900 text-sm sm:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}
